import type { Responsibility, SeatAssignment, SeatDetail } from './types'

/**
 * Card sizing lives in one place because three surfaces have to agree on it:
 * the rendered node, the tree layout (which needs a height before the card is
 * on screen), and the PDF export. The card renders at exactly the height these
 * helpers report, so the layout never has to measure the DOM.
 */

export type CardDensity = 'expanded' | 'collapsed'

export const CARD_WIDTH = 260

/** Titles shown on a card before the "+N more" indicator takes over. */
export const MAX_CARD_RESPONSIBILITIES = 4

/** Owners named on a card before the "+N more" indicator takes over. */
export const MAX_CARD_OWNERS = 3

const CARD_BORDER_Y = 2 // 1px top + 1px bottom
const BODY_PADDING_Y = 24 // p-3
const TITLE_ROW_H = 24 // title line / actions button
export const STATUS_ROW_H = 28 // mt-2 + status badge row
const RESPONSIBILITY_BLOCK_GAP = 8 // mt-2
const RESPONSIBILITY_LINE_H = 20 // leading-5
const OWNERS_BLOCK_GAP = 8 // mt-2
const OWNER_ROW_H = 24 // avatar + name line (h-6)
const OWNER_ROW_GAP = 4 // gap-1 between owner rows
const OWNER_OVERFLOW_H = 16 // leading-4 "+N more"
const TAGS_ROW_H = 28 // mt-2 + tag chips

/** Height of the per-branch expand/collapse strip at the bottom of a card. */
export const BRANCH_TOGGLE_H = 26

/**
 * The chart never opens zoomed out past this, even when the whole tree would
 * only fit further out: below it the obsession names on a card stop being
 * readable, which is the whole point of listing them there. A tree too big for
 * this floor opens legible and is panned, not squinted at.
 */
export const MIN_LEGIBLE_ZOOM = 0.8

/** Space left between the chart canvas and the edges of the pane, in px. */
export const CHART_FIT_PADDING = 32

/**
 * Height of the bottom-left cluster — zoom buttons, minimap and zoom readout —
 * that floats over the canvas. The opening frame keeps clear of it so a card
 * does not land under a panel that swallows the clicks aimed at it.
 */
export const CHART_BOTTOM_CLUSTER_H = 150

export function cardResponsibilities(seat: SeatDetail): Responsibility[] {
  return seat.responsibilities.slice(0, MAX_CARD_RESPONSIBILITIES)
}

export function hiddenResponsibilityCount(seat: SeatDetail): number {
  return Math.max(0, seat.responsibilities.length - MAX_CARD_RESPONSIBILITIES)
}

/** Lines the responsibility block occupies, including its empty state. */
export function responsibilityLineCount(seat: SeatDetail): number {
  if (seat.responsibilities.length === 0) return 1
  return cardResponsibilities(seat).length + (hiddenResponsibilityCount(seat) > 0 ? 1 : 0)
}

/**
 * Owners named on the card, in saved assignment order (primary holder first).
 * allow-unsorted-people: never alphabetized.
 */
export function cardAssignees(seat: SeatDetail): SeatAssignment[] {
  return seat.assignments.slice(0, MAX_CARD_OWNERS)
}

export function hiddenAssigneeCount(seat: SeatDetail): number {
  return Math.max(0, seat.assignments.length - MAX_CARD_OWNERS)
}

/**
 * A filled role carries no status badge: the owner names beneath it already
 * say the role is filled, so the badge was only noise. Every other status
 * still earns its line, because it flags a position needing attention.
 * The card, the height helper and the PDF export all read this one rule.
 */
export function showsStatusRow(seat: SeatDetail): boolean {
  return seat.seat_status !== 'filled'
}

/**
 * Height of the owner block, which is on the card at both densities. Owners
 * are named rows now, so the block grows with the number of owners and the
 * layout can no longer assume one fixed avatar strip.
 */
export function ownersBlockHeight(seat: SeatDetail): number {
  const rows = cardAssignees(seat).length
  if (rows === 0) return 0

  const overflow = hiddenAssigneeCount(seat) > 0 ? OWNER_ROW_GAP + OWNER_OVERFLOW_H : 0
  return OWNERS_BLOCK_GAP + rows * OWNER_ROW_H + (rows - 1) * OWNER_ROW_GAP + overflow
}

export interface CardSizeInput {
  density: CardDensity
  hasChildren: boolean
}

export function seatCardHeight(seat: SeatDetail, { density, hasChildren }: CardSizeInput): number {
  const branchToggle = hasChildren ? BRANCH_TOGGLE_H : 0
  const header = CARD_BORDER_Y + BODY_PADDING_Y + TITLE_ROW_H +
    (showsStatusRow(seat) ? STATUS_ROW_H : 0)
  const owners = ownersBlockHeight(seat)

  // Collapsed still answers "who owns this": role name, any status worth
  // flagging, owners.
  if (density === 'collapsed') return header + owners + branchToggle

  return (
    header +
    owners +
    RESPONSIBILITY_BLOCK_GAP +
    responsibilityLineCount(seat) * RESPONSIBILITY_LINE_H +
    (seat.tags.length > 0 ? TAGS_ROW_H : 0) +
    branchToggle
  )
}
